import { Constants } from './Layout';
import type { Portfolio, SubjectRangeAnalysis } from 'venn-api';
import type { HistoricalNodeTaggedWithRange, StrategyOrPortfolioWithRange } from './types';
import { assert, assertNotNil } from 'venn-utils';
import type { HistoricalPortfolioContextValue } from './AllocationPanelContext';

export const getFixedWidthElementsWidth = ({
  isStrategy,
  hasAddFund,
  hideComparisonColumn,
}: {
  isStrategy: boolean;
  hasAddFund: boolean;
  hideComparisonColumn: boolean;
}): number => {
  return (
    (isStrategy ? Constants.TRIANGLE_WIDTH : 0) +
    Constants.NAME_MARGIN +
    (hasAddFund ? Constants.ADD_FUND_PLUS_WIDTH : 0) +
    Constants.ALLOCATION_WIDTH +
    Constants.ACTIONS_WIDTH +
    (hideComparisonColumn ? 0 : Constants.COMPARE_WIDTH)
  );
};

function* getFundAnalysis(rangeAnalysis: SubjectRangeAnalysis) {
  // rangeAnalyses are flat and only apply to funds
  yield* rangeAnalysis.rangeAnalyses;
}

export const augmentPortfolioWithRangeAnalysis = ({
  portfolio,
  rangeAnalysis,
}: {
  portfolio: Portfolio;
  rangeAnalysis: SubjectRangeAnalysis;
}): StrategyOrPortfolioWithRange => {
  assert(portfolio.historical, 'Portfolio must be historical');
  const helper = (
    portfolio: Portfolio,
    analysisGenerator: Generator<SubjectRangeAnalysis, void>,
  ): HistoricalNodeTaggedWithRange => {
    if (portfolio.fund) {
      // funds have no children
      const fundAnalysis = analysisGenerator.next();
      assert(!fundAnalysis.done, 'Missing range analysis for some fund');
      return {
        _tag: 'fund',
        ...portfolio,
        allocation: assertNotNil(portfolio.allocation),
        historical: true,
        closingAllocationsTs: assertNotNil(portfolio.closingAllocationsTs as [number, number][]),
        fund: {
          ...assertNotNil(portfolio.fund),
          startRange: fundAnalysis.value.start,
          endRange: fundAnalysis.value.end,
          returnFrequency: fundAnalysis.value.frequency,
        },
        children: [],
        rangeAnalysis: fundAnalysis.value,
      };
    }
    return {
      _tag: 'strategy',
      ...portfolio,
      allocation: assertNotNil(portfolio.allocation),
      historical: true,
      children: portfolio.children.map((child) => helper(child, analysisGenerator)),
      fund: undefined,
    };
  };
  const result = helper(portfolio, getFundAnalysis(rangeAnalysis));
  assert(result._tag === 'strategy', 'Expected portfolio as root');
  return result;
};

export const hasData = (value: HistoricalPortfolioContextValue) => {
  return value.status === 'success';
};
